// httpClient.js
import { loadToken, saveToken, removeToken } from './utils/cache'
import axios from 'axios'

// 模拟Token存储和获取的函数
const TokenService = {
	getAccessToken: () => loadToken(),
	setTokens: (accessToken) => {
		saveToken(accessToken)
	},
	clearTokens: () => {
		removeToken()
	}
}

function pickErrorMessage(data) {
	if (!data) return ''
	if (typeof data === 'string') return data
	return data.message || data.msg || data.error || ''
}

function isAuthError(status, data) {
	// HTTP 401，或业务码 TOKEN_INVALID(401) / 鉴权相关(10001+)
	// 注意：1000 是通用业务错误（如表单校验），不能当作登录失效
	if (status === 401) return true
	const code = data && parseInt(data.code, 10)
	return code === 401 || code === 10001 || code === 10002 || code === 10004
}

function notifyAuthExpired(statusCallbacks) {
	TokenService.clearTokens()
	const cb = statusCallbacks && statusCallbacks[401]
	if (cb) cb()
}

// =================================================================
// 核心 HttpClient 类
// =================================================================

class HttpClient {
	constructor({ baseURL, timeout = 15000 }, headers = {}, statusCallbacks = {}) {
		if (HttpClient.instance) {
			return HttpClient.instance
		}

		this.statusCallbacks = statusCallbacks

		this.api = axios.create({
			baseURL: baseURL,
			timeout: timeout,
			headers: {
				...headers,
				'Content-Type': 'application/json'
			}
		})

		this._initializeInterceptors()

		HttpClient.instance = this
	}

	_initializeInterceptors() {
		this.api.interceptors.request.use(
			(config) => {
				const token = TokenService.getAccessToken()
				if (token) {
					config.headers['Authorization'] = `Bearer ${token}`
				}
				return config
			},
			(error) => Promise.reject(error)
		)

		this.api.interceptors.response.use(
			(res) => {
				if (res.status === 200) {
					const data = res.data
					if (parseInt(data.code, 10) === 0) {
						return Promise.resolve(data.data)
					}
					if (isAuthError(res.status, data)) {
						notifyAuthExpired(this.statusCallbacks)
						return Promise.reject(data)
					}
					return Promise.reject(data)
				}
				return Promise.reject(res)
			},
			async (error) => {
				const originalRequest = error.config
				const status = error.response?.status
				const data = error.response?.data

				if (isAuthError(status, data)) {
					notifyAuthExpired(this.statusCallbacks)
					return Promise.reject(error)
				}

				if (this.statusCallbacks && status && this.statusCallbacks[status]) {
					this.statusCallbacks[status]()
					return Promise.reject(error)
				}

				if (originalRequest && originalRequest.alertError === false) {
					return Promise.reject(error)
				}

				const msg =
					pickErrorMessage(data) ||
					error.message ||
					(status ? `连接出错(${status})` : '网络异常')
				alert(`请求失败：${msg}`)
				return Promise.reject(error)
			}
		)
	}

	get(url, config) {
		return this.api.get(url, config)
	}

	post(url, data, config) {
		return this.api.post(url, data, config)
	}

	put(url, data, config) {
		return this.api.put(url, data, config)
	}

	delete(url, config) {
		return this.api.delete(url, config)
	}

	static getInstance(config, headers, statusCallbacks) {
		if (!HttpClient.instance) {
			HttpClient.instance = new HttpClient(config, headers, statusCallbacks)
		}
		return HttpClient.instance
	}

	static resetInstance() {
		HttpClient.instance = null
	}
}

export default HttpClient
